import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';

/**
 * Responsive DatePicker for Mobile devices falling back to Native Date inputs
 */
export const ResponsiveDatePicker = ({ value, onChange, disabledDate, ...props }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isMobile) {
    const dateStr = value && dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : '';
    return (
      <input
        type='date'
        className='ant-input'
        value={dateStr}
        onChange={(e) => {
          const val = e.target.value;
          if (onChange) {
            onChange(val ? dayjs(val) : null);
          }
        }}
        min={disabledDate ? dayjs().format('YYYY-MM-DD') : undefined}
        style={{
          width: '100%',
          height: '38px',
          padding: '4px 11px',
          fontSize: '14px',
          lineHeight: '1.5',
          color: 'rgba(0, 0, 0, 0.88)',
          backgroundColor: '#ffffff',
          backgroundImage: 'none',
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          transition: 'all 0.2s',
          boxSizing: 'border-box',
        }}
      />
    );
  }

  return <DatePicker value={value} onChange={onChange} disabledDate={disabledDate} {...props} />;
};

ResponsiveDatePicker.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
  disabledDate: PropTypes.func,
};

/**
 * Responsive Date RangePicker for Mobile devices falling back to Native Date inputs
 */
export const ResponsiveRangePicker = ({ value, onChange, ...props }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isMobile) {
    const [start, end] = value || [null, null];
    const startStr = start && dayjs(start).isValid() ? dayjs(start).format('YYYY-MM-DD') : '';
    const endStr = end && dayjs(end).isValid() ? dayjs(end).format('YYYY-MM-DD') : '';

    const handleStartChange = (e) => {
      const val = e.target.value;
      const newStart = val ? dayjs(val).startOf('day') : null;
      if (onChange) {
        onChange([newStart, end]);
      }
    };

    const handleEndChange = (e) => {
      const val = e.target.value;
      const newEnd = val ? dayjs(val).endOf('day') : null;
      if (onChange) {
        onChange([start, newEnd]);
      }
    };

    const inputStyle = {
      flex: 1,
      height: '38px',
      padding: '4px 11px',
      fontSize: '14px',
      color: 'rgba(0, 0, 0, 0.88)',
      backgroundColor: '#ffffff',
      border: '1px solid #d9d9d9',
      borderRadius: '6px',
      boxSizing: 'border-box',
      textAlign: 'center',
    };

    return (
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
        <input
          type='date'
          value={startStr}
          onChange={handleStartChange}
          max={endStr || dayjs().format('YYYY-MM-DD')}
          style={inputStyle}
        />
        <span style={{ color: '#8c8c8c', fontSize: '13px' }}>ถึง</span>
        <input
          type='date'
          value={endStr}
          onChange={handleEndChange}
          min={startStr}
          max={dayjs().format('YYYY-MM-DD')}
          style={inputStyle}
        />
      </div>
    );
  }

  return <DatePicker.RangePicker value={value} onChange={onChange} {...props} />;
};

ResponsiveRangePicker.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func,
};
